<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-surface-raised border-b border-border px-6 py-4">
            <div class="max-w-3xl mx-auto">
                <BasePageTitle no-border :title="t('assessments.schneider.title')" :subtitle="t('assessments.schneider.intro.subtitle')" />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <Transition name="fade" mode="out-in">
                <div v-if="phase === 'instructions'" key="instructions" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-list-check text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <!-- FIXME: Consider extracting assessment section headers to a shared BasePageHeader component -->
                            <h2 class="title-body-bold text-ink">{{ t("assessments.schneider.intro.instructions.heading") }}</h2>
                        </div>
                        <p class="body text-ink mb-4">
                            {{ t("assessments.schneider.intro.instructions.groundedIn") }}
                        </p>
                        <p class="body text-ink mb-4">
                            {{ t("assessments.schneider.intro.instructions.ratingScale") }}
                        </p>
                        <p class="body text-ink">{{ t("assessments.schneider.intro.instructions.exampleLead") }}</p>
                    </div>
                </div>

                <div v-else-if="phase === 'example'" key="example" class="max-w-3xl mx-auto px-6 py-10 space-y-5">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-eye text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-ink">{{ t("assessments.schneider.intro.example.heading") }}</h2>
                        </div>
                        <p class="body-small text-ink-muted mb-6">
                            {{ t("assessments.schneider.intro.example.lede") }}
                        </p>
                        <div class="bg-surface-sunken rounded-xl border border-border p-5 mb-6">
                            <div class="flex items-start justify-between gap-4 mb-3">
                                <div class="flex-1">
                                    <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                                    <p class="details font-bold tracking-widest text-ink uppercase mb-1">{{ t("assessments.schneider.intro.example.competency") }}</p>
                                    <h3 class="body-bold text-ink">{{ t("assessments.schneider.intro.example.question") }}</h3>
                                </div>
                                <div class="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-border text-ink-muted">
                                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- em dash placeholder for an unrated badge; punctuation only -->
                                    <span class="title-body-bold leading-none">—</span>
                                </div>
                            </div>
                            <div class="mt-3">
                                <div class="h-1 rounded-full bg-border w-full mb-2"></div>
                                <div class="flex justify-between px-[10px]">
                                    <span v-for="n in 5" :key="n" data-testid="example-rating-point" class="text-[10px] text-ink-muted font-semibold">{{
                                        t("assessments.schneider.intro.example.ratingPoint", { rating: n, label: rating_labels[String(n)] ?? "" })
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="body-small-bold text-ink mb-3">{{ t("assessments.schneider.intro.example.scaleHeading") }}</p>
                        <div class="divide-y divide-border">
                            <div v-for="n in 5" :key="n" class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-surface-sunken text-ink details flex items-center justify-center">
                                    {{ n }}
                                </div>
                                <div>
                                    <span class="body-small-bold text-ink">{{ rating_labels[String(n)] }}</span>
                                    <!-- The leading " " is the gap after the bold label; Vue drops whitespace-only text at the start of an element. -->
                                    <span class="body-small text-ink-muted">{{
                                        " " + t("assessments.schneider.intro.example.scaleDescription", { description: rating_descriptions[String(n)] ?? "" })
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <div class="shrink-0 bg-surface-raised border-t border-border px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <BaseButton v-if="phase !== 'instructions'" theme="tertiary" :disabled="isStartingAssessment" @click="emit('intro-prev')">
                    {{ t("assessments.schneider.intro.back") }}
                </BaseButton>
                <div v-else></div>
                <BaseButton theme="primary" :disabled="isStartingAssessment" @click="emit('intro-next')">
                    {{ nextButtonLabel }}
                </BaseButton>
            </div>
            <div v-if="startError" role="alert" class="max-w-3xl mx-auto mt-2 details text-notification-warning-text">
                {{ startError }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

type IntroPhase = "instructions" | "example";

interface Props {
    phase: IntroPhase;
    isStartingAssessment: boolean;
    startError: string | null;
    rating_labels: Record<string, string>;
    rating_descriptions: Record<string, string>;
}

const props = defineProps<Props>();

const nextButtonLabel = computed(() => {
    if (props.phase !== "example") {
        return t("assessments.schneider.intro.continue");
    }
    return props.isStartingAssessment ? t("assessments.schneider.intro.starting") : t("assessments.schneider.intro.start");
});

const emit = defineEmits<{
    (e: "intro-prev"): void;
    (e: "intro-next"): void;
}>();
</script>
